/**
 * Local-Lab Conversion Tile — Admin Dashboard.
 *
 * Feb 2026 — Shows the split between mail-out and lab-attendance
 * fulfilment options for the last 30 days so the admin can see the
 * All-Inclusive Pricing pilot's impact at a glance. Powered by
 * GET /api/admin/analytics/local-lab-conversion.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, MapPin, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";


export function LocalLabConversionTile({ days = 30 }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/admin/analytics/local-lab-conversion?days=${days}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const totals = data?.totals || {};
  const total = data?.total_bookings ?? 0;
  const labBookings = data?.lab_choice_bookings ?? 0;
  const adoption = data?.lab_adoption_pct ?? 0;
  const topPostcodes = (data?.top_postcodes || []).filter((r) => r.postcode !== "unknown").slice(0, 5);

  return (
    // Feb 2026 — plain <div> card, NOT a wrapping <Link>. The earlier
    // full-card <Link> caused unrelated hover/focus artefacts from
    // sibling tiles to visually leak "clickable pills" into this
    // tile's bounds on some browsers — every click inside the card
    // routed to /admin/settings/pricing-and-fulfilment. A dedicated
    // footer link (below) is the safer, less-surprising pattern
    // matching ReviewFunnelTile.
    <div
      data-testid="kpi-tile-local-lab-conversion"
      className="rounded-3xl bg-white border border-slate-200 p-6"
    >
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-4 h-4 text-cyan-600" />
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
          Local-lab adoption · last {days} days
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-3">
            <div
              data-testid="kpi-tile-local-lab-adoption-pct"
              className="text-5xl font-display font-bold text-slate-900 leading-none tabular-nums"
            >
              {adoption.toFixed(1)}%
            </div>
            <div className="text-xs text-slate-500">
              <div>lab-attendance choice</div>
              <div className="tabular-nums">
                <span data-testid="kpi-tile-local-lab-lab-count">{labBookings}</span>
                {" / "}
                <span data-testid="kpi-tile-local-lab-total">{total}</span>
                {" paid bookings"}
              </div>
            </div>
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4 grid gap-6 md:grid-cols-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-2">
                Fulfilment split
              </div>
              <div className="space-y-1.5" data-testid="kpi-tile-local-lab-splits">
                <SplitRow icon={Truck}  label="Mail-Out Kit"          count={totals.aware_mailout ?? 0}         total={total} testId="split-aware_mailout" />
                <SplitRow icon={MapPin} label="Lab Pickup & Return"   count={totals.lab_collection_return ?? 0} total={total} testId="split-lab_collection_return" />
                <SplitRow icon={MapPin} label="Bring-Your-Own Bottle" count={totals.customer_lab_dropoff ?? 0}  total={total} testId="split-customer_lab_dropoff" />
                <SplitRow icon={Truck}  label="Partner-Supplied Kit"  count={totals.partner_supplied ?? 0}      total={total} testId="split-partner_supplied" />
              </div>
            </div>
            {topPostcodes.length > 0 ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-2">Top postcodes</div>
                <div className="space-y-1" data-testid="kpi-tile-local-lab-top-postcodes">
                  {topPostcodes.map((row) => (
                    <div
                      key={row.postcode}
                      data-testid={`top-postcode-${row.postcode}`}
                      className="flex items-center justify-between text-xs gap-3"
                    >
                      <span className="font-mono text-slate-700 shrink-0">{row.postcode}</span>
                      <span className="text-slate-500 tabular-nums text-right">
                        {row.total} booking{row.total === 1 ? "" : "s"} · <span className={row.lab_adoption_pct > 0 ? "text-cyan-700 font-semibold" : "text-slate-400"}>{row.lab_adoption_pct.toFixed(0)}% lab</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-xs text-slate-400 italic">No bookings with an identifiable postcode yet.</div>
            )}
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <div>
              {(data?.all_inclusive_bookings ?? 0) > 0 && (
                <>
                  <span className="tabular-nums font-semibold text-slate-700" data-testid="kpi-tile-local-lab-all-inclusive-count">
                    {data.all_inclusive_bookings}
                  </span>
                  {" "}on the All-Inclusive Pricing model
                </>
              )}
            </div>
            <Link
              to="/admin/settings/pricing-and-fulfilment"
              data-testid="kpi-tile-local-lab-open-settings"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-cyan-50 text-xs font-semibold text-slate-700 border border-slate-200"
            >
              Open Pricing &amp; Fulfilment <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}


function SplitRow({ icon: Icon, label, count, total, testId }) {
  const pct = total ? (100.0 * count / total) : 0;
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-slate-600 min-w-0">
        <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="flex items-center gap-2 tabular-nums shrink-0">
        <span className="text-slate-500 text-xs">{pct.toFixed(0)}%</span>
        <span className="font-semibold text-slate-900">{count}</span>
      </div>
    </div>
  );
}
